import { FC, useMemo } from "react"
import {
  VictoryAxis,
  VictoryChart,
  VictoryContainer,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryLabel,
} from "victory"
import { colors } from "../../utils/theme"
import { ChartDataPoints } from "../../utils/types"

import ChartTooltip from "../atoms/ChartTooltip"
import {
  TREND_CHART,
  chartAxisLabelStyle,
  chartTickLabelStyle,
} from "./charts/chartTypography"

const ActivityLegendData = [
  {
    name: "Walking",
    symbol: {
      fill: colors.trendingData.orange.dark,
    },
  },
  {
    name: "Exercise",
    symbol: {
      fill: colors.trendingData.blue.dark,
    },
  },
  {
    name: "Stationary",
    symbol: {
      fill: colors.trendingData.red.dark,
    },
  },
  {
    name: "In Transit",
    symbol: {
      fill: colors.trendingData.green.dark,
    },
  },
]

const ActivitiesChart: FC<{
  activities: ChartDataPoints[][][]
  categories: string[]
}> = ({ activities, categories }) => {
  /// Calculate the included activity labels to be used in the legend
  const includedActivities = useMemo(() => {
    // const included = activities.map((activity) =>
    //   activity.map((data) => data[0]?.label)
    // )
    const included: string[] = activities.reduce((acc, activity) => {
      console.log(activity[0][0])
      if (activity[0][0]?.name) {
        acc.push(activity[0][0]!.name)
      }
      return acc
    }, [] as string[])
    console.log(included)
    return ActivityLegendData.filter((activity) =>
      included.includes(activity.name)
    )
  }, [activities])

  /// An array of four ticks from max + 10% of max to 0
  const chartTicks: number[] = useMemo(() => {
    const max = Math.max(
      ...activities.map((activity) =>
        Math.max(...activity.map((data) => Math.max(...data.map(({ y }) => y))))
      )
    )
    let chartMax = Math.ceil(max * 1.1)
    chartMax = chartMax > 1440 ? 1440 : chartMax // 1440 is the max minutes in a day
    chartMax = chartMax < 16 ? 16 : chartMax // 16 is an arbitrary minimum
    return [
      0,
      Math.round(chartMax * 0.25),
      Math.round(chartMax * 0.5),
      Math.round(chartMax * 0.75),
      chartMax,
    ]
  }, [activities])

  return (
    <div
      style={{
        marginTop: 16,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <VictoryChart
        height={TREND_CHART.height}
        width={TREND_CHART.width}
        domainPadding={{ x: [0, 48] }}
        padding={{ top: 16, bottom: 64, left: 65, right: 16 }}
        containerComponent={
          <VictoryContainer
            responsive
            style={{ width: "100%", height: "auto" }}
          />
        }
      >
        <VictoryAxis
          orientation="top"
          tickComponent={<></>}
          tickLabelComponent={<></>}
          style={{
            axis: { stroke: colors.neutral.clay },
          }}
        />
        <VictoryAxis
          orientation="right"
          dependentAxis={true}
          tickComponent={<></>}
          tickLabelComponent={<></>}
          style={{
            axis: { stroke: colors.neutral.clay },
          }}
        />
        <VictoryAxis
          orientation="bottom"
          tickFormat={(tick, index) => (index % 2 !== 0 ? tick : "")}
          style={{
            tickLabels: chartTickLabelStyle,
            axis: { stroke: colors.neutral.clay },
          }}
        />
        <VictoryAxis
          orientation="left"
          dependentAxis={true}
          // tickValues={[0, 360, 720, 1080, 1440]}
          label="Minutes"
          axisLabelComponent={<VictoryLabel angle={-90} />}
          tickValues={chartTicks}
          style={{
            tickLabels: chartTickLabelStyle,
            axisLabel: chartAxisLabelStyle,
            grid: { stroke: colors.neutral.clay },
            axis: { stroke: colors.neutral.clay },
          }}
        />

        {activities.map((activity) =>
          activity.map((data, index) => (
            <VictoryLine
              key={index}
              categories={{
                x: categories,
              }}
              style={{
                data: {
                  stroke: data[0]?.darkColor,
                  strokeWidth: 2,
                },
              }}
              data={data.map(({ x, y }) => ({ x, y }))}
            />
          ))
        )}

        {activities.map((activity) =>
          activity.map((data, index) => (
            <VictoryScatter
              key={index}
              categories={{
                x: categories,
              }}
              data={data}
              symbol="circle"
              size={6}
              style={{
                data: {
                  stroke: data[0]?.darkColor,
                  strokeWidth: 2,
                  fill: data[0]?.lightColor,
                },
              }}
              labelComponent={
                <VictoryTooltip
                  flyoutComponent={
                    <ChartTooltip
                      width={190}
                      otherData={activities}
                      otherDataRange={180}
                      crosshair={{
                        chartHeight: 265,
                        paddingTop: 16,
                        paddingBottom: 64,
                      }}
                    />
                  }
                />
              }
            />
          ))
        )}
      </VictoryChart>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "8px 24px",
          marginTop: 8,
          width: "100%",
          minWidth: 0,
        }}
      >
        {includedActivities.map((activity) => (
          <div
            key={activity.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: activity.symbol.fill,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: "Lato",
                color: colors.neutral.asphalt,
                fontSize: 14,
                fontWeight: 400,
              }}
            >
              {activity.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ActivitiesChart
